import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { GUIDES } from '@/constants/corey';
import { PERSONALITIES } from '@/constants/personalities';
import { useBuddyVoice } from '@/lib/voice';
import { Check, X, Loader2, Sparkles } from 'lucide-react';

/**
 * GuidePickerModal — 2-card selector for choosing Corey or Christine.
 * onClose: called after successful pick OR user dismissal.
 * If dismissible=false, close button is hidden (used for first-login flow).
 */
export default function GuidePickerModal({ onClose, dismissible = true, autoPick = null }) {
  const { user, setGuide, setPersonality } = useAuth();
  const [saving, setSaving] = useState(null);
  const [savingPers, setSavingPers] = useState(null);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [introPlaying, setIntroPlaying] = useState(false);
  const flashTimerRef = useRef(null);
  const wasFirstPickRef = useRef(!user?.guide); // track if this session started with no buddy
  const autoPickTriggeredRef = useRef(false);
  const current = user?.guide;
  const currentPers = user?.personality || 'friendly_pro';
  const { speakQuip } = useBuddyVoice();

  useEffect(() => () => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
  }, []);

  // Auto-trigger pick() once if a pre-selected buddy was passed via prop (e.g. from
  // the landing-page buddy card → signup → /app?autoBuddy=corey flow). This fires
  // the full pick flow including the intro voice.
  useEffect(() => {
    if (autoPick && !autoPickTriggeredRef.current && !current && !saving) {
      autoPickTriggeredRef.current = true;
      pick(autoPick);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPick, current]);

  const showFlash = (msg) => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setFlash(msg);
    flashTimerRef.current = setTimeout(() => {
      setFlash('');
      flashTimerRef.current = null;
    }, 1800);
  };

  const pick = async (id) => {
    setError('');
    setSaving(id);
    try {
      const isFirst = !current; // no buddy set before this pick → this is the intro moment
      await setGuide(id);
      const name = id.charAt(0).toUpperCase() + id.slice(1);
      showFlash(`${name} is now your buddy ✓`);
      // First-time buddy pick → have them introduce themselves out loud
      if (isFirst && wasFirstPickRef.current) {
        setIntroPlaying(true);
        try {
          await speakQuip('intro', {
            user_name: user?.name || null,
          });
        } finally {
          setIntroPlaying(false);
        }
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Could not save preference';
      console.error('setGuide failed:', e);
      setError(msg);
    } finally {
      setSaving(null);
    }
  };

  const pickPers = async (id) => {
    setError('');
    setSavingPers(id);
    try {
      await setPersonality(id);
      const p = PERSONALITIES.find((x) => x.id === id);
      if (p) showFlash(`Vibe: ${p.label} ✓`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Could not save vibe';
      console.error('setPersonality failed:', e);
      setError(msg);
    } finally {
      setSavingPers(null);
    }
  };

  // NOTE: We intentionally do NOT dismiss on backdrop click. Reason: on mobile,
  // an errant tap near the edge of a buddy card would silently dismiss the modal
  // without picking. Users can close via the X button (top-right) or "Done" button.

  return (
    <div
      data-testid="guide-picker-modal"
      className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 fade-up overflow-y-auto"
    >
      <div
        className="relative bg-zinc-900 border border-zinc-800 max-w-3xl w-full my-4 sm:my-8 p-4 sm:p-8 border-t-4 border-t-amber-400 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {dismissible && (
          <button
            onClick={onClose}
            className="absolute top-2 right-2 sm:top-3 sm:right-3 text-zinc-500 hover:text-zinc-100 p-1 z-10 bg-zinc-900/80"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        )}

        <div className="text-center mb-4 sm:mb-6 pr-8">
          <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-amber-400 mb-2 flex items-center justify-center gap-2">
            <Sparkles size={12} className="pulse-gentle" /> Pick Your DIY Buddy
          </div>
          <h2 className="font-display text-2xl sm:text-4xl text-zinc-100 tracking-tight leading-tight">
            WHO'S HELPING YOU TODAY?
          </h2>
          <p className="text-zinc-400 mt-2 text-xs sm:text-sm">
            Both know their stuff. Pick your vibe — you can switch any time.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:gap-4">
          {Object.values(GUIDES).map((g) => {
            const isCurrent = current === g.id;
            const isLoading = saving === g.id;
            return (
              <button
                key={g.id}
                data-testid={`guide-pick-${g.id}`}
                onClick={() => pick(g.id)}
                disabled={!!saving}
                className={`industrial-card !p-0 overflow-hidden text-left transition-all group ${
                  isCurrent
                    ? 'border-lime-400 shadow-lg shadow-lime-500/20'
                    : 'hover:border-amber-500 hover:-translate-y-1'
                } ${saving && !isLoading ? 'opacity-40' : ''}`}
              >
                <div className="relative aspect-[3/4] bg-zinc-950 overflow-hidden">
                  <img
                    src={g.imageUrl}
                    alt={g.name}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
                    style={{ objectPosition: 'center 20%' }}
                  />
                  {isCurrent && (
                    <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-lime-400 text-zinc-950 font-mono text-[9px] sm:text-[10px] uppercase tracking-widest px-2 py-1 flex items-center gap-1">
                      <Check size={10} strokeWidth={3} /> <span className="hidden sm:inline">Current</span>
                    </div>
                  )}
                  {isLoading && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                      <Loader2 size={28} className="text-amber-400 animate-spin" />
                    </div>
                  )}
                </div>
                <div className="p-3 sm:p-4 bg-zinc-900">
                  <div className="font-display text-lg sm:text-2xl text-zinc-100 tracking-wide">{g.name}</div>
                  <div className="text-zinc-400 text-xs sm:text-sm line-clamp-2">{g.description}</div>
                </div>
              </button>
            );
          })}
        </div>

        {error && (
          <div className="text-red-400 text-sm mt-4 font-mono text-center">{error}</div>
        )}

        {/* Personality selector */}
        <div className="mt-6 sm:mt-8 border-t border-zinc-800 pt-4 sm:pt-6">
          <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-lime-400 mb-3 flex items-center gap-2">
            <Sparkles size={12} /> Pick Their Vibe
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PERSONALITIES.map((p) => {
              const active = currentPers === p.id;
              const busy = savingPers === p.id;
              return (
                <button
                  key={p.id}
                  data-testid={`personality-${p.id}`}
                  onClick={() => pickPers(p.id)}
                  disabled={!!savingPers}
                  className={`border p-2 text-left transition-colors relative ${
                    active ? 'border-lime-400 bg-lime-500/10' : 'border-zinc-800 hover:border-amber-500'
                  } ${savingPers && !busy ? 'opacity-40' : ''}`}
                >
                  <div className="text-base sm:text-lg flex items-center justify-between">
                    <span>{p.emoji}</span>
                    {active && <Check size={12} className="text-lime-400" strokeWidth={3} />}
                    {busy && <Loader2 size={12} className="text-amber-400 animate-spin" />}
                  </div>
                  <div className="font-mono text-[9px] sm:text-[10px] uppercase tracking-widest text-zinc-100 mt-1 truncate">{p.label}</div>
                  <div className="text-[9px] sm:text-[10px] text-zinc-500 truncate">{p.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Success flash */}
        {flash && (
          <div
            data-testid="guide-picker-flash"
            className="mt-4 text-center font-mono text-xs uppercase tracking-widest text-lime-400 border border-lime-500/40 bg-lime-500/10 py-2"
          >
            {flash}
          </div>
        )}

        {/* Buddy introducing themselves */}
        {introPlaying && (
          <div
            data-testid="guide-picker-intro-playing"
            className="mt-3 text-center font-mono text-xs uppercase tracking-widest text-amber-400 border border-amber-500/40 bg-amber-500/10 py-2 flex items-center justify-center gap-2"
          >
            <Loader2 size={12} className="animate-spin" />
            {current ? `${current === 'christine' ? 'Christine' : 'Corey'} is saying hi…` : 'Getting your buddy on the mic…'}
          </div>
        )}

        {/* Done button — clear way out. Only enabled once a buddy is picked. */}
        <button
          onClick={onClose}
          data-testid="guide-picker-done"
          disabled={!current || introPlaying}
          className="btn-primary w-full mt-4 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {!current
            ? 'Pick Corey or Christine to continue'
            : introPlaying
            ? "Hold on — they're introducing themselves…"
            : "Done — Let's Fix Stuff"}
        </button>

        <div className="mt-3 text-center font-mono text-[9px] sm:text-[10px] uppercase tracking-widest text-zinc-500">
          Sponsored by Contact Electric · Lake Worth · 561-441-2397
        </div>
      </div>
    </div>
  );
}
